import {
  CalendarMonth,
  Explicit,
  Lightbulb,
  LocationOn,
  PendingActions,
  RateReview,
  School,
  Science,
  ArrowBack,
  AccessTime,
  PersonAdd,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Stack,
  Typography,
  useTheme,
  Modal,
  TextField,
  MenuItem,
  Snackbar,
  Rating,
  Autocomplete,
  alpha,
  Container,
  Divider,
  InputAdornment,
  Alert,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { createRequest, clearState } from "../../state/slices/request";
import { CircleLoader } from "react-spinners";
import { extractId } from "../../utils/imageIdExtractor";
import DateAvailabilityCalendar from "../../components/calendar/DateAvailabilityCalendar";
import GoogleMapEmbed from "../../components/map/GoogleMapEmbed";
import { getMyChildrenAPI } from "../../services/child";
import { getMyEmployeesAPI } from "../../services/business";
import { getStudentByUserIdAPI } from "../../services/student";

const DetailResult = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const person = location.state.person;
  const searchFilters = location.state?.searchFilters || {};
  
  const theme = useTheme();
  const { t } = useTranslation();
  const userState = useSelector((state) => state.user);
  const [open, setOpen] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const requestState = useSelector((state) => state.request);
  const [displayComments, setDisplayComments] = React.useState(false);
  const [openAvailability, setOpenAvailability] = React.useState(false);
  const [canRequestTutoring, setCanRequestTutoring] = React.useState(true);
  const [loadingStudentInfo, setLoadingStudentInfo] = React.useState(false);
  const [isChild, setIsChild] = React.useState(false); // true if child (has parentId), false if employee (has businessId)
  const isTutorInactive =
    person?.currentStatus === t('tutorProfile.inActive') || person?.currentStatus === 'In Active';

  // Check if student can request tutoring
  React.useEffect(() => {
    if (userState.user?.roles?.includes('STUDENT') && !userState.user?.roles?.includes('PARENT') && !userState.user?.roles?.includes('BUSINESS')) {
      setLoadingStudentInfo(true);
      getStudentByUserIdAPI(userState.user.id)
        .then((res) => {
          const student = res.data.student;
          setCanRequestTutoring(student?.canRequestTutoring !== false);
          // Determine if it's a child (has parentId) or employee (has businessId)
          setIsChild(!!student?.parentId);
        })
        .catch(() => {
          setCanRequestTutoring(true); // Default to true on error
          setIsChild(false);
        })
        .finally(() => setLoadingStudentInfo(false));
    }
  }, [userState.user]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const RequestModal = () => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user.user);
    const isParent = user?.roles?.includes("PARENT");
    const isBusiness = user?.roles?.includes("BUSINESS");
    const [children, setChildren] = useState([]);
    const [selectedStudentIds, setSelectedStudentIds] = useState([]);
    const [loadingChildren, setLoadingChildren] = useState(false);
    
    useEffect(() => {
      if (isParent || isBusiness) {
        setLoadingChildren(true);
        (isParent ? getMyChildrenAPI() : getMyEmployeesAPI())
          .then((res) => {
            const list = res.data.children || res.data.employees || [];
            // Show all active children/employees (not just those with canRequestTutoring)
            // Parents/businesses can create requests for all children/employees
            setChildren(list.filter(c => c.accountStatus !== "FROZEN" && c.accountStatus !== "DELETED"));
          })
          .catch(() => {})
          .finally(() => setLoadingChildren(false));
      }
    }, [isParent, isBusiness]);

    const [startTime, setStartTime] = React.useState(searchFilters.startTime || "");
    const [endTime, setEndTime] = React.useState(searchFilters.endTime || "");
    // Pre-populate tutoring type from search filters
    const initialTutoringType = React.useMemo(() => {
      if (searchFilters.tutoringOptions && searchFilters.tutoringOptions.length > 0) {
        return searchFilters.tutoringOptions;
      }
      return [];
    }, []);
    const [tutoringType, setTutoringType] = React.useState(initialTutoringType);
    const [message, setMessage] = React.useState("");
    const [date, setDate] = React.useState(searchFilters.date || "");
    const [duration, setDuration] = React.useState(searchFilters.duration || "");
    const [errors, setErrors] = React.useState({});

    const TUTORING_TYPE = {
      IN_PERSON: t('common.inPerson'),
      ONLINE: t('common.online'),
    }

    if (requestState.error || requestState.success) {
      setSnackbarOpen(true);
    }

    const handleFormSubmit = (event) => {
      event.preventDefault();
      const isValid = validate();
      if (isValid) {
        const requestData = {
          tutorId: person.id,
          startTime: startTime || undefined,
          endTime: endTime || undefined,
          tutoringType: tutoringType,
          message: message,
          date: date || undefined,
          duration: duration || undefined,
        };
        if ((isParent || isBusiness) && selectedStudentIds.length > 0) {
          requestData.studentIds = selectedStudentIds;
        }
        dispatch(createRequest(requestData));
        handleClose();
      }
    };

    const validate = () => {
      let errors = {};
      if (!tutoringType || tutoringType.length === 0) {
        errors["tutoringType"] = t('detailResult.pleaseSelectTutoringType');
      }
      if (!message) {
        errors["message"] = t('detailResult.pleaseEnterMessage');
      }
      if ((isParent || isBusiness) && selectedStudentIds.length === 0) {
        errors["studentIds"] = isParent ? (t('detailResult.selectAtLeastOneChild') || 'Select at least one child') : (t('detailResult.selectAtLeastOneEmployee') || 'Select at least one employee');
      }
      // Validate that selected children/employees can request tutoring (only if they're creating for themselves)
      // Parents/businesses can create requests for all children/employees regardless of canRequestTutoring
      // Validate time window if date is provided
      if (date) {
        if (startTime && endTime) {
          const startMinutes = startTime.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
          const endMinutes = endTime.split(':').reduce((h, m) => parseInt(h) * 60 + parseInt(m));
          if (endMinutes <= startMinutes) {
            errors["endTime"] = t('detailResult.endTimeMustBeAfterStartTime') || 'End time must be after start time';
          }
        }
      }

      setErrors(errors);
      return Object.keys(errors).length === 0;
    };



    return (
      <Box>
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={3000}
          onClose={() => {
            setSnackbarOpen(false);
            dispatch(clearState());
          }}
          message={
            <Typography fontWeight={600} fontSize={20} color="secondary">
              {requestState.error
                ? requestState.error.msg
                : requestState.success
                  ? requestState.success.msg
                  : ""}
            </Typography>
          }
        ></Snackbar>
        <Modal 
          open={open} 
          onClose={handleClose}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Card
            elevation={24}
            sx={{
              p: 4,
              borderRadius: 4,
              maxWidth: 600,
              width: '90%',
              maxHeight: "90vh",
              overflowY: "auto",
              background: 'white',
              boxShadow: `0 20px 60px ${alpha('#000000', 0.3)}`,
            }}
          >
            <form onSubmit={handleFormSubmit}>
              <Box sx={{ mb: 3, textAlign: 'center' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    p: 2,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    mb: 2,
                  }}
                >
                  <PersonAdd sx={{ fontSize: 40, color: theme.palette.primary.main }} />
                </Box>
                <Typography variant="h4" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('detailResult.requestTutoring')}
                </Typography>
              </Box>
              <Stack spacing={3}>
                <Autocomplete
                  fullWidth
                  multiple
                  id="tutoringType"
                  options={Object.values(TUTORING_TYPE)}
                  getOptionLabel={(option) => option}
                  value={tutoringType.map(type => {
                    if (type === "IN_PERSON") return TUTORING_TYPE.IN_PERSON;
                    if (type === "REMOTE") return TUTORING_TYPE.ONLINE;
                    return type;
                  })}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('detailResult.tutoringType')}
                      placeholder={t('detailResult.tutoringType')}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                        }
                      }}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={index}
                        label={option}
                        {...getTagProps({ index })}
                        sx={{
                          my: 1,
                          bgcolor: alpha(theme.palette.secondary.main, 0.2),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                        }}
                      />
                    ))
                  }
                  onChange={(event, value) => {
                    value = value.map((type) => {
                      if (type === t('common.inPerson')) {
                        return "IN_PERSON";
                      } else {
                        return "REMOTE";
                      }
                    });
                    setTutoringType(value);
                  }}
                  error={Boolean(errors["tutoringType"])}
                  helperText={errors["tutoringType"]}
                />

                <Box>
                  <TextField
                    fullWidth
                    label={t('detailResult.date') || 'Date'}
                    type="date"
                    value={date}
                    onChange={(event) => {
                      setDate(event.target.value);
                      if (!event.target.value) {
                        setStartTime("");
                        setEndTime("");
                      }
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      mb: date ? 2 : 0,
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                  {/* Time Window - Show when date is selected */}
                  {date && (
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('home.startTime') || 'Start Time'}
                          type="time"
                          value={startTime}
                          onChange={(event) => setStartTime(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors["startTime"])}
                          helperText={errors["startTime"]}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTime sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          fullWidth
                          label={t('home.endTime') || 'End Time'}
                          type="time"
                          value={endTime}
                          onChange={(event) => setEndTime(event.target.value)}
                          InputLabelProps={{ shrink: true }}
                          error={Boolean(errors["endTime"])}
                          helperText={errors["endTime"]}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <AccessTime sx={{ fontSize: 18, color: theme.palette.primary.main }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                            }
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>

                <TextField
                  id="duration"
                  select
                  label={t('detailResult.duration') || 'Duration'}
                  value={duration}
                  onChange={(event) => setDuration(event.target.value)}
                  fullWidth
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                >
                  <MenuItem value="">{t('detailResult.selectDuration') || 'Select Duration'}</MenuItem>
                  <MenuItem value="ONE_TIME">{t('home.oneTime')}</MenuItem>
                  <MenuItem value="RECURRING">{t('home.recurring')}</MenuItem>
                </TextField>

                {(isParent || isBusiness) && (
                  <Box>
                    {loadingChildren ? (
                      <Typography>Loading...</Typography>
                    ) : children.length === 0 ? (
                      <Alert severity="warning">
                        {isParent 
                          ? (t('detailResult.noChildrenWarning') || 'You need to add at least one child before requesting tutoring. Go to Children page to add children.')
                          : (t('detailResult.noEmployeesWarning') || 'You need to add at least one employee before requesting tutoring. Go to Employees page to add employees.')}
                      </Alert>
                    ) : (
                      <Autocomplete
                        multiple
                        id="student-selector"
                        options={children}
                        getOptionLabel={(option) => {
                          const name = [option.userId?.firstName, option.userId?.lastName].filter(Boolean).join(" ") || option.userId?.username || "—";
                          return name;
                        }}
                        value={children.filter(child => {
                          const childId = child._id?.toString() || child._id;
                          return selectedStudentIds.some(id => id?.toString() === childId);
                        })}
                        onChange={(event, newValue) => {
                          setSelectedStudentIds(newValue.map(child => child._id?.toString() || child._id));
                        }}
                        isOptionEqualToValue={(option, value) => {
                          const optionId = option._id?.toString() || option._id;
                          const valueId = value._id?.toString() || value._id;
                          return optionId === valueId;
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label={isParent ? (t('detailResult.selectChildren') || 'Select Children') : (t('detailResult.selectEmployees') || 'Select Employees')}
                            placeholder={isParent ? (t('detailResult.selectChildrenPlaceholder') || 'Select one or more children') : (t('detailResult.selectEmployeesPlaceholder') || 'Select one or more employees')}
                            error={!!errors["studentIds"]}
                            helperText={errors["studentIds"]}
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                              }
                            }}
                          />
                        )}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => {
                            const name = [option.userId?.firstName, option.userId?.lastName].filter(Boolean).join(" ") || option.userId?.username || "—";
                            return (
                              <Chip
                                {...getTagProps({ index })}
                                key={option._id}
                                label={name}
                                size="small"
                              />
                            );
                          })
                        }
                        sx={{
                          '& .MuiAutocomplete-inputRoot': {
                            borderRadius: 2,
                          }
                        }}
                      />
                    )}
                  </Box>
                )}

                <TextField
                  fullWidth
                  label={t('detailResult.message')}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  error={!!errors.message}
                  helperText={errors.message}
                  multiline
                  rows={4}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                    }
                  }}
                />

                {requestState.loading ? (
                  <Button 
                    variant="contained" 
                    fullWidth
                    size="large"
                    disabled
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                    }}
                  >
                    <CircleLoader size={20} />
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    onClick={handleFormSubmit}
                    sx={{
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                      color: theme.palette.secondary.main,
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '1.1rem',
                      boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: `0 6px 25px ${alpha(theme.palette.primary.main, 0.5)}`,
                      }
                    }}
                  >
                    {t('detailResult.submitRequest')}
                  </Button>
                )}
              </Stack>
            </form>
          </Card>
        </Modal>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
        py: 6,
      }}
    >
      <RequestModal />
      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(-1)}
          sx={{
            mb: 3,
            color: theme.palette.primary.main,
            fontWeight: 600,
            '&:hover': {
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }
          }}
        >
          {t('common.back')}
        </Button>

        {/* Profile Header Card */}
        <Card
          elevation={0}
          sx={{
            borderRadius: 4,
            p: 4,
            mb: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              width: '300px',
              height: '300px',
              background: alpha('#ffffff', 0.1),
              borderRadius: '50%',
              transform: 'translate(30%, -30%)',
            }
          }}
        >
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} sm="auto">
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <Avatar
                  src={person.profileImage?.includes('drive.google.com') ? `https://drive.lienuc.com/uc?id=${extractId(person.profileImage)}` : person.profileImage}
                  alt={`${person.firstName} ${person.lastName}`}
                  sx={{
                    width: 150,
                    height: 150,
                    border: '4px solid white',
                    boxShadow: `0 8px 30px ${alpha('#000000', 0.3)}`,
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm>
              <Stack spacing={2}>
                <Typography variant="h3" sx={{ fontWeight: 700 }}>
                  {person.firstName} {person.lastName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ 
                    px: 2, 
                    py: 0.5, 
                    borderRadius: 3, 
                    bgcolor: alpha('#ffffff', 0.2),
                    backdropFilter: 'blur(10px)',
                  }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>
                      {person.rate ? `${person.rate} ETB / ${t('result.hour')}` : 'N/A'}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Rating value={person.ratings || 0} readOnly size="large" />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      ({(person.ratings || 0).toFixed(2)}) {person.totalReviews || 0} {t('result.reviews')}
                    </Typography>
                  </Box>
                </Box>
                {(() => {
                  const isTutor = userState.user?.roles?.includes('TUTOR');
                  const isLoggedIn = !!userState.user;
                  const isApproved = person.approved === "APPROVED";
                  
                  if (isApproved && isLoggedIn && !isTutor) {
                    const isStudent = userState.user?.roles?.includes('STUDENT') && !userState.user?.roles?.includes('PARENT') && !userState.user?.roles?.includes('BUSINESS');
                    const isDisabled = isStudent && !canRequestTutoring;
                    
                    return (
                      <Box>
                        <Button
                          variant="contained"
                          size="large"
                          onClick={handleOpen}
                          disabled={isDisabled || loadingStudentInfo}
                          startIcon={<PersonAdd />}
                          sx={{
                            bgcolor: isDisabled ? alpha('#ffffff', 0.15) : 'white',
                            color: isDisabled ? alpha('#ffffff', 0.6) : theme.palette.primary.main,
                            fontWeight: 700,
                            py: 1.5,
                            borderRadius: 3,
                            maxWidth: 300,
                            border: isDisabled ? `1px solid ${alpha('#ffffff', 0.2)}` : 'none',
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            '&:hover': {
                              bgcolor: isDisabled ? alpha('#ffffff', 0.15) : alpha('#ffffff', 0.9),
                              transform: isDisabled ? 'none' : 'translateY(-2px)',
                            },
                            '&.Mui-disabled': {
                              bgcolor: alpha('#ffffff', 0.15),
                              color: alpha('#ffffff', 0.6),
                            }
                          }}
                        >
                          {t('detailResult.requestTutoring')}
                        </Button>
                        {isDisabled && (
                          <Alert 
                            severity="info" 
                            sx={{ 
                              mt: 1, 
                              maxWidth: 300,
                              bgcolor: alpha('#ffffff', 0.15),
                              border: `1px solid ${alpha('#ffffff', 0.3)}`,
                              color: '#ffffff',
                              '& .MuiAlert-icon': {
                                color: '#ffffff'
                              },
                              '& .MuiAlert-message': {
                                color: '#ffffff',
                                fontWeight: 500
                              }
                            }}
                          >
                            {isChild 
                              ? (t('detailResult.requestDisabledByParent') || 'Request tutoring is disabled by parent')
                              : (t('detailResult.requestDisabledByBusiness') || 'Request tutoring is disabled by business/organization')}
                          </Alert>
                        )}
                      </Box>
                    );
                  } else if (isLoggedIn && isTutor) {
                    return (
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {t('detailResult.tutorsCannotRequest') || 'Tutors cannot request tutoring'}
                      </Typography>
                    );
                  } else if (isLoggedIn) {
                    return (
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {t('detailResult.tutorWaitingApproval')}
                      </Typography>
                    );
                  } else {
                    return (
                      <Typography variant="body1" sx={{ opacity: 0.9 }}>
                        {t('detailResult.loginToRequestTutoring')}
                      </Typography>
                    );
                  }
                })()}
              </Stack>
            </Grid>
          </Grid>
        </Card>

        <Grid container spacing={4}>
          {/* Education Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                height: '100%',
                bgcolor: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <School sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('detailResult.education')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Stack spacing={2}>
                {person.education ? (
                  <>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {t('detailResult.university')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {person.education?.universityName || 'N/A'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                        {t('detailResult.department')}
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                        {person.education?.department || 'N/A'}
                      </Typography>
                    </Box>
                  </>
                ) : (
                  <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    {t('detailResult.noEducationInfo') || 'No education information available'}
                  </Typography>
                )}
              </Stack>
            </Card>
          </Grid>

          {/* Experience Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                height: '100%',
                bgcolor: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Explicit sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('detailResult.experience')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Typography
                variant="body1"
                sx={{
                  color: 'text.secondary',
                  lineHeight: 1.8,
                  textAlign: 'justify',
                }}
              >
                {person.experience || t('detailResult.noExperienceInfo') || 'No experience information available'}
              </Typography>
            </Card>
          </Grid>

          {/* Subjects Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                bgcolor: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Science sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('detailResult.subjects')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {(() => {
                  // Handle both old format (array of strings) and new format (array of objects)
                  const subjectsList = person.subjects && person.subjects.length > 0
                    ? (typeof person.subjects[0] === 'string'
                        ? person.subjects.map(s => ({ subject: s, gradeLevel: '' }))
                        : person.subjects.map(s => ({
                            subject: s.subject || s,
                            gradeLevel: s.gradeLevel || ''
                          })).filter(s => s.subject))
                    : [];
                  
                  if (subjectsList.length === 0) {
                    return (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('detailResult.noSubjects') || 'No subjects available'}
                      </Typography>
                    );
                  }
                  
                  return subjectsList.map((subjectObj, idx) => {
                    const label = subjectObj.gradeLevel 
                      ? `${subjectObj.subject} (${subjectObj.gradeLevel})`
                      : subjectObj.subject;
                    return (
                      <Chip
                        key={idx}
                        label={label}
                        sx={{
                          bgcolor: alpha(theme.palette.secondary.main, 0.2),
                          color: theme.palette.primary.main,
                          fontWeight: 600,
                          fontSize: 16,
                          border: `1px solid ${alpha(theme.palette.secondary.main, 0.5)}`,
                        }}
                      />
                    );
                  });
                })()}
              </Box>
            </Card>
          </Grid>

          {/* Location & Details Card */}
          <Grid item xs={12} md={6}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                bgcolor: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <LocationOn sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                  {t('detailResult.location')}
                </Typography>
              </Box>
              <Divider sx={{ mb: 3 }} />
              <Stack spacing={3}>
                <Box>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main, mb: 1 }}>
                    {person.location || 'N/A'}
                  </Typography>
                  {/* Google Maps Embed */}
                  {person.location && (
                    <GoogleMapEmbed location={person.location} height={300} />
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Lightbulb sx={{ fontSize: 20 }} />
                    {t('detailResult.tutoringType')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {person.tutoringType && person.tutoringType.length > 0 ? (
                      person.tutoringType.map((type) => (
                        <Chip
                          key={type}
                          label={type === "IN_PERSON" ? t('common.inPerson') : t('common.online')}
                          sx={{
                            bgcolor: alpha(theme.palette.primary.main, 0.1),
                            color: theme.palette.primary.main,
                            fontWeight: 600,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`,
                          }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        N/A
                      </Typography>
                    )}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PendingActions sx={{ fontSize: 20 }} />
                    {t('detailResult.verificationStatus')}
                  </Typography>
                  <Chip
                    label={person.approved ? person.approved.charAt(0).toUpperCase() + person.approved.slice(1).toLowerCase() : 'N/A'}
                    sx={{
                      bgcolor: person.approved === "APPROVED" ? alpha('#4caf50', 0.1) : alpha('#ff9800', 0.1),
                      color: person.approved === "APPROVED" ? '#4caf50' : '#ff9800',
                      fontWeight: 700,
                    }}
                  />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarMonth sx={{ fontSize: 20 }} />
                    {t('detailResult.availability')}
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                    {person.availability || 'N/A'}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonAdd sx={{ fontSize: 20 }} />
                    {t('detailResult.rate')}
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                    {person.rate ? `${person.rate} ${t('result.etbPerHour')}` : 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </Card>
          </Grid>
          {!isTutorInactive && (
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  p: 4,
                  bgcolor: 'white',
                  boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <CalendarMonth sx={{ fontSize: 32, color: theme.palette.primary.main, mr: 2 }} />
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {t('tutorProfile.calendar')}
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    startIcon={<CalendarMonth />}
                    onClick={() => setOpenAvailability(!openAvailability)}
                    sx={{
                      borderRadius: 2,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: theme.palette.primary.dark,
                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                      }
                    }}
                  >
                    {openAvailability ? t('detailResult.hideAvailability') : t('detailResult.showAvailability')}
                  </Button>
                </Box>
                <Divider sx={{ mb: 3 }} />
                {openAvailability && (
                  <DateAvailabilityCalendar
                    value={person.dateAvailability || []}
                    disabled={true}
                  />
                )}
              </Card>
            </Grid>
          )}

          {/* Reviews Section */}
          <Grid item xs={12}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                p: 4,
                bgcolor: 'white',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.1)}`,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <RateReview sx={{ fontSize: 32, color: theme.palette.primary.main }} />
                  <Box>
                    <Typography variant="h5" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                      {t('detailResult.reviews')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      <Rating value={person.ratings || 0} readOnly size="small" />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600 }}>
                        ({(person.ratings || 0).toFixed(2)}) {person.reviews?.length || 0} {t('result.reviews')}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
                <Button
                  variant="outlined"
                  onClick={() => setDisplayComments(!displayComments)}
                  sx={{
                    borderRadius: 2,
                    borderColor: theme.palette.primary.main,
                    color: theme.palette.primary.main,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: theme.palette.primary.dark,
                      bgcolor: alpha(theme.palette.primary.main, 0.05),
                    }
                  }}
                >
                  {displayComments ? t('detailResult.hideComments') : t('detailResult.showComments')}
                </Button>
              </Box>
              <Divider sx={{ mb: 3 }} />
              {displayComments && (
                <Stack spacing={2}>
                  {!person.reviews || person.reviews.length === 0 ? (
                    <Typography variant="body1" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                      {t('detailResult.noReviews')}
                    </Typography>
                  ) : (
                    person.reviews.map((review, idx) => (
                      <Card
                        key={review.id || idx}
                        elevation={0}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          bgcolor: alpha(theme.palette.primary.main, 0.05),
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                        }}
                      >
                        <Grid container spacing={2} alignItems="center">
                          <Grid item xs={12} sm={8}>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.primary.main, mb: 1 }}>
                              {review.userId ? `${review.userId?.firstName || ''} ${review.userId?.lastName || ''}`.trim() : 'Anonymous'}
                            </Typography>
                            <Typography variant="body1" sx={{ color: 'text.secondary', lineHeight: 1.8 }}>
                              {review.comment || 'No comment'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={4} sx={{ display: 'flex', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                            <Rating value={review.rating || 0} readOnly size="large" />
                          </Grid>
                        </Grid>
                      </Card>
                    ))
                  )}
                </Stack>
              )}
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default DetailResult;
